import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Plane, Download, Check, X as XIcon, DollarSign } from 'lucide-react';
import { fmtUSD, REIMB_STATUS_STYLES, todayISO } from './utils';
import { API_BASE } from '../../utils/apiBase';
import logger from '../../utils/logger';

const EMPTY = { user_id: '', user_name: '', trip_date: todayISO(), from_location: '', to_location: '', miles: '', purpose: '', amount: '', receipt_url: '', notes: '' };

export default function ReimbursementsTab({ api, onRefresh }) {
  const [list, setList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [config, setConfig] = useState({ mileage_rate: 0.67 });
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState('pending');

  const fetchAll = useCallback(async () => {
    try {
      const [l, e, c] = await Promise.all([api.get('/finance/reimbursements'), api.get('/employees'), api.get('/finance/config')]);
      setList(l.data); setEmployees(e.data); setConfig(c.data);
    } catch (err) { logger.error(err); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialog(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r }); setDialog(true); };

  const miles = parseFloat(form.miles) || 0;
  const computedAmount = (miles * (config.mileage_rate || 0.67)).toFixed(2);

  const save = async () => {
    try {
      if (!form.miles && !form.amount) { toast.error('Miles or amount required'); return; }
      if (!form.trip_date) { toast.error('Trip date required'); return; }
      if (editing) { await api.put(`/finance/reimbursements/${editing.id}`, form); toast.success('Updated'); }
      else { await api.post('/finance/reimbursements', form); toast.success('Submitted'); }
      setDialog(false); fetchAll(); onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const decide = async (r, status) => {
    try {
      await api.put(`/finance/reimbursements/${r.id}/decision`, { status });
      toast.success(`Marked ${status}`);
      fetchAll(); onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this reimbursement?')) return;
    await api.delete(`/finance/reimbursements/${id}`); fetchAll(); onRefresh?.();
  };

  const filtered = list.filter(r => filter === 'all' || r.status === filter);
  const totals = {
    pending: list.filter(r => r.status === 'pending').reduce((s, r) => s + (r.amount || 0), 0),
    approved: list.filter(r => r.status === 'approved').reduce((s, r) => s + (r.amount || 0), 0),
    paid_mtd: list.filter(r => r.status === 'paid' && (r.approved_at || '') >= todayISO().slice(0, 7)).reduce((s, r) => s + (r.amount || 0), 0),
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-3">
          <Card className="border-[#FFB74D] bg-[#FFF3E0]"><CardContent className="p-3"><p className="text-[10px] uppercase tracking-wider text-[#E65100] font-semibold">Pending</p><p className="text-lg font-bold text-[#2D1B4E]">{fmtUSD(totals.pending)}</p></CardContent></Card>
          <Card className="border-[#64B5F6] bg-[#E3F2FD]"><CardContent className="p-3"><p className="text-[10px] uppercase tracking-wider text-[#1565C0] font-semibold">Approved</p><p className="text-lg font-bold text-[#2D1B4E]">{fmtUSD(totals.approved)}</p></CardContent></Card>
          <Card className="border-[#81C784] bg-[#E8F5E9]"><CardContent className="p-3"><p className="text-[10px] uppercase tracking-wider text-[#1B5E20] font-semibold">Paid MTD</p><p className="text-lg font-bold text-[#2D1B4E]">{fmtUSD(totals.paid_mtd)}</p></CardContent></Card>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.open(`${API_BASE}/api/finance/export/reimbursements`, '_blank')} className="border-[#DED8EB] gap-1 h-9" data-testid="reimb-export-btn"><Download className="h-3.5 w-3.5" /> CSV</Button>
          <Button onClick={openCreate} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1 h-9" data-testid="reimb-add-btn"><Plus className="h-4 w-4" /> Log Trip</Button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[['all','All'],['pending','Pending'],['approved','Approved'],['paid','Paid'],['denied','Denied']].map(([k, l]) => (
          <Button key={k} size="sm" variant={filter === k ? 'default' : 'outline'} onClick={() => setFilter(k)}
            className={filter === k ? 'bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white h-8' : 'border-[#DED8EB] h-8'}
            data-testid={`reimb-filter-${k}`}>{l}</Button>
        ))}
      </div>

      {loading ? <p className="text-sm text-[#8E85A0]">Loading…</p> : filtered.length === 0 ? (
        <div className="text-center py-10 text-[#8E85A0] border border-dashed border-[#DED8EB] rounded-lg">
          <Plane className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No reimbursements to show.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="reimb-list">
          {filtered.map(r => (
            <Card key={r.id} className="border shadow-sm bg-white" data-testid={`reimb-row-${r.id}`}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-bold text-[#2D1B4E]">{r.user_name}</h3>
                      <Badge className={`text-[10px] border ${REIMB_STATUS_STYLES[r.status]}`}>{r.status}</Badge>
                      <span className="text-[10px] text-[#8E85A0]">{r.trip_date}</span>
                    </div>
                    <p className="text-xs text-[#5A4D6E] mt-0.5">
                      {r.from_location} → {r.to_location} · <span style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{r.miles}mi @ ${r.mileage_rate}/mi</span>
                    </p>
                    {r.purpose && <p className="text-[11px] text-[#8E85A0] mt-0.5 italic">{r.purpose}</p>}
                    {r.approver_name && <p className="text-[10px] text-[#8E85A0] mt-0.5">✓ {r.status} by {r.approver_name}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{fmtUSD(r.amount)}</p>
                    {r.status === 'pending' && (
                      <>
                        <Button size="icon" variant="ghost" onClick={() => decide(r, 'approved')} title="Approve" className="h-7 w-7 text-[#1B5E20] hover:bg-[#E8F5E9]" data-testid={`reimb-approve-${r.id}`}><Check className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => decide(r, 'denied')} title="Deny" className="h-7 w-7 text-[#D9534F] hover:bg-red-50" data-testid={`reimb-deny-${r.id}`}><XIcon className="h-4 w-4" /></Button>
                      </>
                    )}
                    {r.status === 'approved' && (
                      <Button size="icon" variant="ghost" onClick={() => decide(r, 'paid')} title="Mark paid" className="h-7 w-7 text-[#2E7D32] hover:bg-[#E8F5E9]" data-testid={`reimb-paid-${r.id}`}><DollarSign className="h-4 w-4" /></Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => openEdit(r)} className="h-7 w-7 text-[#8E85A0] hover:text-[#6B3FA0]" data-testid={`reimb-edit-${r.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(r.id)} className="h-7 w-7 text-[#8E85A0] hover:text-[#D9534F]" data-testid={`reimb-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="sm:max-w-lg bg-white" data-testid="reimb-dialog">
          <DialogHeader><DialogTitle className="text-lg font-bold text-[#2D1B4E]">{editing ? 'Edit Trip' : 'Log Trip'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Staff</Label>
              <Select value={form.user_id} onValueChange={v => {
                const emp = employees.find(e => e.id === v);
                setForm(f => ({ ...f, user_id: v, user_name: emp?.name || '' }));
              }}>
                <SelectTrigger className="border-[#DED8EB]" data-testid="reimb-user"><SelectValue placeholder="Select staff (or leave blank for self)" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Trip Date</Label><Input type="date" value={form.trip_date} onChange={e => setForm(f => ({ ...f, trip_date: e.target.value }))} className="border-[#DED8EB]" data-testid="reimb-date" /></div>
              <div><Label>Miles</Label><Input type="number" step="0.1" value={form.miles} onChange={e => setForm(f => ({ ...f, miles: e.target.value }))} className="border-[#DED8EB]" data-testid="reimb-miles" /></div>
              <div><Label>From</Label><Input value={form.from_location} onChange={e => setForm(f => ({ ...f, from_location: e.target.value }))} placeholder="Office" className="border-[#DED8EB]" data-testid="reimb-from" /></div>
              <div><Label>To</Label><Input value={form.to_location} onChange={e => setForm(f => ({ ...f, to_location: e.target.value }))} placeholder="Client site" className="border-[#DED8EB]" data-testid="reimb-to" /></div>
            </div>
            <div><Label>Purpose</Label><Input value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))} placeholder="Client visit, training, etc." className="border-[#DED8EB]" data-testid="reimb-purpose" /></div>
            <div><Label>Amount Override ($) <span className="text-xs text-[#8E85A0] font-normal">— leave blank to auto-compute @ ${config.mileage_rate}/mi</span></Label>
              <Input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder={`Auto: $${computedAmount}`} className="border-[#DED8EB]" data-testid="reimb-amount" />
            </div>
            <div><Label>Receipt URL</Label><Input value={form.receipt_url} onChange={e => setForm(f => ({ ...f, receipt_url: e.target.value }))} placeholder="https://…" className="border-[#DED8EB]" data-testid="reimb-receipt" /></div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="border-[#DED8EB]" data-testid="reimb-notes" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button><Button onClick={save} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="reimb-save-btn">{editing ? 'Update' : 'Submit'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
